import React, { useState, useEffect } from "react";
import VisualizationOptionsModal from './VisualizationOptionsModal';
import { Settings, User, Plus } from "lucide-react";
import { useChat } from './ChatContext';
import { Project, Visualization } from '../types/api';

interface LeftSidebarProps {
  onOpenSettings: () => void;
  onOpenUserModal: () => void;
}

interface ProjectChangedDetail {
  projectId: string;
  projectName: string;
}

interface VisualizationChangedDetail {
  visualizationId: string;
  visualizationName: string;
  projectId: string;
}

interface VisualizationAddedDetail {
  projectId: string;
  visualizationId: string;
  visualizationName: string;
}

export default function LeftSidebar({ onOpenSettings, onOpenUserModal }: LeftSidebarProps) {
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [visualizations, setVisualizations] = useState<Visualization[]>([]);
  const [selectedVisualizationId, setSelectedVisualizationId] = useState<string | null>(null);
  const [modalViz, setModalViz] = useState<Visualization | null>(null);
  const [newVizName, setNewVizName] = useState<string>('');
  const { startNewChat } = useChat();
  const handleNewVisualization = async () => {
    if (!selectedProjectId) {
      console.warn('No project selected for new visualization');
      startNewChat();
      return;
    }
    const projectId = selectedProjectId;
    try {
      const resp = await fetch(`/api/visualizations?projectid=${projectId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      const data: Visualization[] = await resp.json();
      if (resp.ok && Array.isArray(data) && data.length > 0) {
        const newViz = data[0];
        window.dispatchEvent(new CustomEvent('visualizationAdded', {
          detail: { projectId, visualizationId: newViz.visualizationId, visualizationName: newViz.visualizationName }
        }));
      } else if (!resp.ok) {
        console.error('Failed to create visualization', data);
      }
    } catch (error) {
      console.error('Error creating visualization:', error);
    }
    startNewChat();
  };

  // Fetch projects for the current user
  const fetchProjects = () => {
    const userObj = localStorage.getItem("userSelectedObj");
    if (!userObj) return setProjects([]);
    const userId = JSON.parse(userObj).userId;
    fetch(`/api/projects?userid=${userId}`)
      .then((res) => res.json())
      .then((data: Project[]) => {
        setProjects(data);
      })
      .catch(() => setProjects([]));
  };

  // Function to load visualizations for current project
  const loadVisualizations = () => {
    const userObj = localStorage.getItem("userSelectedObj");
    if (!userObj || !selectedProjectId) {
      setVisualizations([]);
      return;
    }
    fetch(`/api/visualizations?projectid=${selectedProjectId}`)
      .then(res => res.json())
      .then((data: Visualization[]) => setVisualizations(Array.isArray(data) ? data : []))
      .catch(() => setVisualizations([]));
  };

  // Context menu to rename or delete a visualization
  const handleVizContextMenu = (e: React.MouseEvent, viz: Visualization) => {
    e.preventDefault();
    setModalViz(viz);
    setNewVizName(viz.visualizationName);
  };

  const handleModalClose = () => {
    setModalViz(null);
    setNewVizName('');
  };

  const handleDeleteVisualization = async () => {
    if (!modalViz) return;
    if (!window.confirm('Confirm deletion?')) return;
    try {
      const resp = await fetch(`/api/visualizations?visualizationid=${modalViz.visualizationId}`, { method: 'DELETE' });
      if (resp.ok) {
        loadVisualizations();
        window.dispatchEvent(new CustomEvent('visualizationDeleted', {
          detail: { visualizationId: modalViz.visualizationId }
        }));
        if (selectedVisualizationId === modalViz.visualizationId) setSelectedVisualizationId(null);
      }
    } catch (err) {
      console.error('Error deleting visualization:', err);
    }
    handleModalClose();
  };

  const handleRenameVisualization = async () => {
    if (!modalViz || !newVizName.trim()) return;
    try {
      const resp = await fetch(`/api/visualizations?visualizationid=${modalViz.visualizationId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newName: newVizName.trim() })
      });
      if (resp.ok) {
        loadVisualizations();
        window.dispatchEvent(new CustomEvent('visualizationRenamed', {
          detail: { visualizationId: modalViz.visualizationId, visualizationName: newVizName.trim() }
        }));
      }
    } catch (err) {
      console.error('Error renaming visualization:', err);
    }
    handleModalClose();
  };

  useEffect(loadVisualizations, [selectedProjectId]);

  useEffect(() => {
    const handleNewViz = (e: CustomEvent<VisualizationAddedDetail>) => {
      if (e.detail.projectId !== selectedProjectId) return;
      loadVisualizations();
      setSelectedVisualizationId(e.detail.visualizationId);
      window.dispatchEvent(new CustomEvent('visualizationChanged', {
        detail: {
          visualizationId: e.detail.visualizationId,
          visualizationName: e.detail.visualizationName
        }
      }));
    };
    window.addEventListener('visualizationAdded', handleNewViz as EventListener);
    return () => window.removeEventListener('visualizationAdded', handleNewViz as EventListener);
  }, [selectedProjectId]);

  useEffect(() => {
    fetchProjects();
    const handleUserChanged = () => {
      setSelectedProjectId(null);
      setSelectedVisualizationId(null);
      fetchProjects();
    };
    window.addEventListener("userChanged", handleUserChanged as EventListener);
    return () => window.removeEventListener("userChanged", handleUserChanged as EventListener);
  }, []);

  const isSidebarCollapsed = collapsed;

  // Get current user name from localStorage
  let currentUserName = '';
  try {
    const userObj = localStorage.getItem('userSelectedObj');
    if (userObj) {
      const parsed = JSON.parse(userObj);
      currentUserName = parsed.username || parsed.name || '';
    }
  } catch {}
  
  return (
    <div
      className={`bg-uiBackground text-black flex flex-col p-4 relative transition-all duration-1000 ${
        isSidebarCollapsed ? "w-16" : "w-64"
      }`}
    >
      <div className="flex items-center">
        <img
          src="/favicon.ico"
          alt="App Icon"
          className={`w-8 h-8 cursor-pointer${!isSidebarCollapsed ? ' mr-4' : ''}`}
          style={{ borderRadius: 4 }}
          onClick={() => setCollapsed((prev) => !prev)}
          title={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        />
        {!isSidebarCollapsed && (
          <h2 className="text-2xl font-bold whitespace-nowrap">AI Data App</h2>
        )}
      </div>
      <div className="w-full border-b-2 border-black mt-4"></div>
      {/* Projects List */}
      {!isSidebarCollapsed && (
        <div className="">
          <h3 className="text-lg font-semibold mt-2">Projects</h3>
          <ul className="space-y-1">
            {projects.length === 0 ? (
              <li className="text-md p-4 opacity-60">No projects</li>
            ) : (
              projects.map((project, idx) => (
                <React.Fragment key={project.projectId}>
                  <li
                    className={`text-base p-4 rounded cursor-pointer font-medium transition-colors ${
                      selectedProjectId === project.projectId
                        ? "bg-primary-dark text-white"
                        : "hover:bg-secondary-light dark:hover:bg-secondary-dark"
                    }`}
                    onClick={() => {
                      setSelectedProjectId(project.projectId);
                      setSelectedVisualizationId(null);
                      const detail: ProjectChangedDetail = {
                        projectId: project.projectId,
                        projectName: project.projectName || ''
                      };
                      window.dispatchEvent(new CustomEvent('projectChanged', { detail }));
                    }}
                  >
                    {project.projectName}
                    {/* Visualizations for selected project */}
                    {selectedProjectId === project.projectId && (
                      <ul className="ml-2 mt-2 space-y-1">
                        {visualizations.length === 0 ? (
                          <li className="text-sm text-black italic opacity-60">
                            No visualizations yet
                          </li>
                        ) : (
                          visualizations.map((viz, vizIndex) => {
                            const vizId = viz.visualizationId;
                            const vizName = viz.visualizationName;
                            const isSelected = selectedVisualizationId === vizId;
                            return (
                              <li
                                key={vizId || `viz-${vizIndex}`}
                                onContextMenu={e => handleVizContextMenu(e, viz)}
                                className={`text-sm px-2 py-1 rounded cursor-pointer transition-colors ${
                                  isSelected
                                    ? 'border border-black bg-primary-dark text-white'
                                    : 'bg-uiBackground text-black hover:bg-secondary-light dark:hover:bg-secondary-dark'
                                }`}
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                  setSelectedVisualizationId(vizId);
                                  const detail: VisualizationChangedDetail = {
                                    visualizationId: vizId,
                                    visualizationName: vizName,
                                    projectId: selectedProjectId!
                                  };
                                  window.dispatchEvent(new CustomEvent('visualizationChanged', { detail }));
                                }}
                              >
                                {vizName || 'Unnamed Visualization'}
                              </li>
                            );
                          })
                        )}
                        <li className="px-2 py-1">
                          <button
                            type="button"
                            onClick={handleNewVisualization}
                            className="w-full flex items-center gap-2 text-sm px-2 py-1 rounded bg-primary-light dark:bg-primary-dark text-white hover:bg-secondary-dark dark:hover:bg-secondary-light transition-colors duration-200"
                          >
                            <Plus size={16} /> New Visualization
                          </button>
                        </li>
                      </ul>
                    )}
                  </li>
                  {idx < projects.length - 1 && (
                    <div className="border-b border-black mx-2" />
                  )}
                </React.Fragment>
              ))
            )}
          </ul>
        </div>
      )}

      {/* Settings and User button at the bottom */}
      <div
        className="absolute bottom-3 left-3 flex flex-col items-center gap-2"
        style={{ zIndex: 10 }}
      >
        <button
          className="flex items-center justify-center p-2 rounded-full hover:bg-primary-light dark:hover:bg-primary-dark focus:outline-none transition-all duration-200"
          onClick={onOpenUserModal}
          title="Change User"
        >
          <User size={24} />
        </button>
        <button
          className="flex items-center justify-center p-2 rounded-full hover:bg-primary-light dark:hover:bg-primary-dark focus:outline-none transition-all duration-200"
          onClick={onOpenSettings}
          title="Settings"
        >
          <Settings size={24} />
        </button>
      </div>

      {/* Modal for visualization rename/delete */}
      {modalViz && (
        <VisualizationOptionsModal
          viz={modalViz}
          newName={newVizName}
          setNewName={setNewVizName}
          onRename={handleRenameVisualization}
          onDelete={handleDeleteVisualization}
          onClose={handleModalClose}
        />
      )}
    </div>
  );
}
